import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Send, Lock, Crown, MessageCircle, Bell, Check, X, User, Clock, AlertCircle, Smile, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import EmojiPicker from 'emoji-picker-react';
import { OnlineIndicator } from '../components/OnlineIndicator';
import SubscriptionRequired from '../components/SubscriptionRequired';
import { getImageUrl } from '../utils/imageUtils';

export default function MessagesPage() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [voting, setVoting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedValidation, setSelectedValidation] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [deletingConversation, setDeletingConversation] = useState(null);
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const emojiPickerRef = useRef(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  
  const canMessage = user?.permissions?.can_message === true;

  // Handle recipientId passed from MemberProfilePage
  useEffect(() => {
    const initializeWithRecipient = async () => {
      const recipientId = location.state?.recipientId;
      if (recipientId && user) {
        try {
          // Fetch recipient profile to get their info
          const profileRes = await api.get(`/profile/${recipientId}`);
          const recipientProfile = profileRes.data;
          
          // Create a conversation object to select
          const newConversation = {
            partner: {
              id: recipientId,
              pseudo: recipientProfile.pseudo,
              profile_picture: recipientProfile.profile_picture
            },
            profile: recipientProfile,
            is_system: false,
            unread_count: 0
          };
          
          // Set as selected conversation
          setSelectedConversation(newConversation);
          
          // Clear the location state to prevent re-triggering
          navigate('/messages', { replace: true, state: {} });
        } catch (error) {
          console.error('Error fetching recipient profile:', error);
        }
      }
    };
    
    initializeWithRecipient();
  }, [location.state?.recipientId, user]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onEmojiClick = (emojiData) => {
    setNewMessage(prev => prev + emojiData.emoji);
    inputRef.current?.focus();
  };

  const deleteConversation = async (e, partnerId) => {
    e.stopPropagation(); // Prevent selecting the conversation
    
    if (!window.confirm('Voulez-vous vraiment supprimer cette conversation ?')) {
      return;
    }
    
    setDeletingConversation(partnerId);
    try {
      await api.delete(`/messages/conversation/${partnerId}`);
      toast.success('Conversation supprimée');
      
      // Remove from list and clear selection if it was selected
      setConversations(prev => prev.filter(c => c.partner?.id !== partnerId));
      if (selectedConversation?.partner?.id === partnerId) {
        setSelectedConversation(null);
        setMessages([]);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingConversation(null);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, [user]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.partner.id);
    }
  }, [selectedConversation]);

  const fetchConversations = async () => {
    try {
      const response = await api.get('/messages');
      // Transform API response to match frontend expected format
      const transformed = response.data.map(conv => ({
        ...conv,
        partner: {
          id: conv.partner_id,
          pseudo: conv.partner_pseudo,
          profile_picture: conv.partner_profile_picture
        }
      }));
      // Sort to put system messages first if there are unread ones
      const sorted = transformed.sort((a, b) => {
        if (a.is_system && a.unread_count > 0) return -1;
        if (b.is_system && b.unread_count > 0) return 1;
        return 0;
      });
      setConversations(sorted);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Erreur lors du chargement des conversations');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const response = await api.get(`/messages/${userId}`);
      setMessages(response.data);
      // Scroll to bottom after messages load
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      if (error.response?.status === 403) {
        // Non-subscriber trying to view a private conversation
        setShowSubscriptionPopup(true);
        setSelectedConversation(null);
      } else {
        toast.error('Erreur lors du chargement des messages');
      }
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || selectedConversation.is_system) return;

    setSending(true);
    try {
      await api.post('/messages', {
        recipient_id: selectedConversation.partner.id,
        content: newMessage.trim()
      });
      setNewMessage('');
      fetchMessages(selectedConversation.partner.id);
      fetchConversations();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleValidationVote = async (profileUserId, approved, reason = null) => {
    setVoting(true);
    try {
      await api.post('/validation/vote-from-message', {
        profile_id: profileUserId,
        approved,
        rejection_reason: reason
      });
      
      toast.success(approved ? 'Profil validé !' : 'Profil refusé');
      setRejectDialogOpen(false);
      setRejectionReason('');
      setSelectedValidation(null);
      
      // Refresh messages to show updated status
      if (selectedConversation) {
        fetchMessages(selectedConversation.partner.id);
      }
      fetchConversations();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors du vote';
      toast.error(message);
    } finally {
      setVoting(false);
    }
  };

  const renderValidationMessage = (msg) => {
    const isVoted = msg.validation_voted;
    const isExpired = msg.validation_expired;
    const wasApproved = msg.validation_approved;

    return (
      <div className="bg-gradient-to-br from-gold/10 to-gold/5 border border-gold/30 rounded-lg p-4 max-w-[90%]">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-gold" />
          </div>
          <div className="flex-1">
            <p className="text-gold font-semibold text-sm">Demande de validation</p>
            <p className="text-gray-400 text-xs">
              {new Date(msg.created_at).toLocaleDateString('fr-FR', { 
                day: 'numeric', 
                month: 'long',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-4 p-3 bg-dark/50 rounded-lg">
          <Avatar className="w-12 h-12 border border-gold/30">
            {msg.profile_picture ? (
              <AvatarImage src={getImageUrl(msg.profile_picture)} />
            ) : (
              <AvatarFallback className="bg-dark-subtle text-gold">
                {msg.profile_pseudo?.[0]?.toUpperCase() || '?'}
              </AvatarFallback>
            )}
          </Avatar>
          <div>
            <p className="text-white font-semibold">{msg.profile_pseudo}</p>
            <p className="text-gray-500 text-sm">Nouveau membre en attente</p>
          </div>
        </div>

        {isExpired ? (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Clock className="w-4 h-4" />
            <span>Délai expiré - Réassigné à un autre membre</span>
          </div>
        ) : isVoted ? (
          <div className={`flex items-center gap-2 text-sm ${wasApproved ? 'text-green-400' : 'text-red-400'}`}>
            {wasApproved ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
            <span>{wasApproved ? 'Vous avez validé ce profil' : 'Vous avez refusé ce profil'}</span>
          </div>
        ) : (
          <div className="space-y-3">
            <Link 
              to={`/member/${msg.profile_user_id}`}
              className="block text-center text-gold hover:text-gold/80 text-sm underline"
            >
              Voir le profil complet →
            </Link>
            
            <div className="flex gap-2">
              <Button
                onClick={() => handleValidationVote(msg.profile_user_id, true)}
                disabled={voting}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm py-2"
                data-testid={`approve-btn-${msg.profile_user_id}`}
              >
                <Check className="w-4 h-4 mr-1" />
                Valider
              </Button>
              
              <Dialog open={rejectDialogOpen && selectedValidation === msg.id} onOpenChange={(open) => {
                setRejectDialogOpen(open);
                if (open) setSelectedValidation(msg.id);
              }}>
                <DialogTrigger asChild>
                  <Button
                    className="flex-1 bg-red-600/20 hover:bg-red-600/30 text-red-400 text-sm py-2"
                    data-testid={`reject-btn-${msg.profile_user_id}`}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Refuser
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-dark-paper border-white/10">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-xl text-white">
                      Raison du refus
                    </DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    <RadioGroup
                      value={rejectionReason}
                      onValueChange={setRejectionReason}
                      className="space-y-4"
                    >
                      <div className="flex items-center space-x-3">
                        <RadioGroupItem value="photos_not_good" id="photos" />
                        <Label htmlFor="photos" className="text-gray-300">
                          Les photos ne sont pas appropriées
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3">
                        <RadioGroupItem value="description_insufficient" id="description" />
                        <Label htmlFor="description" className="text-gray-300">
                          La description est insuffisante
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3">
                        <RadioGroupItem value="both" id="both" />
                        <Label htmlFor="both" className="text-gray-300">
                          Les deux
                        </Label>
                      </div>
                    </RadioGroup>

                    <Button
                      onClick={() => handleValidationVote(msg.profile_user_id, false, rejectionReason)}
                      disabled={!rejectionReason || voting}
                      className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white"
                    >
                      {voting ? 'Envoi...' : 'Confirmer le refus'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            
            <p className="text-gray-500 text-xs text-center flex items-center justify-center gap-1">
              <Clock className="w-3 h-3" />
              48h pour répondre
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderSystemNotification = (msg) => {
    const isSuccess = msg.content.includes('Félicitations') || msg.content.includes('🎉');
    const isWarning = msg.content.includes('⚠️') || msg.content.includes('Amélioration');
    
    return (
      <div className={`rounded-lg p-4 max-w-[90%] ${
        isSuccess ? 'bg-green-500/10 border border-green-500/30' :
        isWarning ? 'bg-amber-500/10 border border-amber-500/30' :
        'bg-dark-subtle border border-white/10'
      }`}>
        <div className="flex items-start gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            isSuccess ? 'bg-green-500/20' :
            isWarning ? 'bg-amber-500/20' :
            'bg-gold/20'
          }`}>
            {isSuccess ? <Check className="w-4 h-4 text-green-400" /> :
             isWarning ? <AlertCircle className="w-4 h-4 text-amber-400" /> :
             <Bell className="w-4 h-4 text-gold" />}
          </div>
          <div className="flex-1">
            <p className="text-white text-sm whitespace-pre-line">{msg.content}</p>
            <span className="text-xs text-gray-500 mt-2 block">
              {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Render a regular message
  const renderMessage = (msg) => {
    const isOwnMessage = msg.sender_id === user?.id || msg.sender_pseudo === user?.pseudo;
    const isValidationRequest = msg.message_type === 'validation_request';
    const isSystemMsg = msg.sender_id === 'system';
    
    // If it's a validation request, use the validation message renderer
    if (isValidationRequest) {
      return renderValidationMessage(msg);
    }
    
    // If it's a system message, use the system message renderer
    if (isSystemMsg) {
      return renderSystemMessage(msg);
    }
    
    // Regular user message
    return (
      <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[80%] p-3 rounded-2xl ${
          isOwnMessage 
            ? 'bg-gold text-dark rounded-br-sm' 
            : 'bg-dark-subtle text-white border border-white/10 rounded-bl-sm'
        }`}>
          <p className="text-sm whitespace-pre-line">{msg.content}</p>
          <div className={`flex items-center gap-1 mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
            <span className={`text-xs ${isOwnMessage ? 'text-dark/60' : 'text-gray-500'}`}>
              {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
            {isOwnMessage && msg.read && (
              <Check className="w-3 h-3 text-dark/60" />
            )}
          </div>
        </div>
      </div>
    );
  };

  // Show subscription popup for non-subscribers trying to message
  // They can see notifications but not send messages
  return (
    <div className="min-h-screen bg-dark pt-20 md:pt-24 pb-20 md:pb-12" data-testid="messages-page">
      {/* Subscription Required Popup */}
      <SubscriptionRequired 
        open={showSubscriptionPopup} 
        onClose={() => setShowSubscriptionPopup(false)} 
        feature="messaging" 
      />
      
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <h1 className="font-serif text-2xl md:text-3xl gold-gradient-text mb-6 md:mb-8">Messages</h1>
        
        {/* Access notice for non-subscribers */}
        {!canMessage && (
          <div className="glass-card p-3 md:p-4 mb-4 md:mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-gold/30">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-gold flex-shrink-0" />
              <p className="text-gray-400 text-sm">
                Vous pouvez voir vos notifications, mais l'envoi de messages nécessite un abonnement.
              </p>
            </div>
            <Link to="/subscription" className="btn-gold text-sm px-4 py-2 flex items-center gap-2 w-full sm:w-auto justify-center">
              <Crown className="w-4 h-4" />
              S'abonner
            </Link>
          </div>
        )}
        
        <div className="glass-card overflow-hidden">
          {/* Mobile: Show conversation list or messages based on selection */}
          <div className="md:hidden">
            {selectedConversation ? (
              <div className="h-[calc(100vh-220px)]">
                {/* Mobile back button and header */}
                <div className="flex items-center gap-3 p-3 border-b border-white/10 bg-dark">
                  <button 
                    onClick={() => setSelectedConversation(null)}
                    className="p-2 -ml-2 text-gray-400 hover:text-white"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <Avatar className="w-8 h-8">
                    {selectedConversation.is_system ? (
                      <AvatarFallback className="bg-gold/20 text-gold">
                        <Bell className="w-4 h-4" />
                      </AvatarFallback>
                    ) : selectedConversation.profile?.profile_picture ? (
                      <AvatarImage src={getImageUrl(selectedConversation.profile?.profile_picture)} />
                    ) : (
                      <AvatarFallback className="bg-dark-subtle text-gold text-sm">
                        {selectedConversation.partner?.pseudo?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <span className="text-white font-medium truncate">
                    {selectedConversation.partner?.pseudo || 'DiscreetLux'}
                  </span>
                </div>
                
                {/* Messages area for mobile */}
                <ScrollArea className="h-[calc(100%-120px)] p-3">
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div key={msg.id}>{renderMessage(msg)}</div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                
                {/* Input for mobile */}
                {!selectedConversation.is_system && canMessage && (
                  <div className="p-3 border-t border-white/10 bg-dark">
                    <form onSubmit={sendMessage} className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Votre message..."
                        className="flex-1 input-gold text-sm"
                      />
                      <Button type="submit" className="btn-gold px-3" disabled={sending}>
                        <Send className="w-4 h-4" />
                      </Button>
                    </form>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-[calc(100vh-220px)]">
                <div className="p-3 border-b border-white/10">
                  <h2 className="text-white font-semibold">Conversations</h2>
                </div>
                <ScrollArea className="h-[calc(100%-49px)]">
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <div className="w-6 h-6 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
                    </div>
                  ) : conversations.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Aucune conversation</p>
                    </div>
                  ) : (
                    <div>
                      {conversations.map((conv) => (
                        <button
                          key={conv.partner?.id || 'system'}
                          onClick={() => setSelectedConversation(conv)}
                          className="w-full p-3 flex items-center gap-3 hover:bg-white/5 transition-colors text-left border-b border-white/5"
                        >
                          <Avatar className={`w-10 h-10 border ${conv.is_system ? 'border-gold' : 'border-white/10'}`}>
                            {conv.is_system ? (
                              <AvatarFallback className="bg-gold/20 text-gold">
                                <Bell className="w-4 h-4" />
                              </AvatarFallback>
                            ) : conv.profile?.profile_picture ? (
                              <AvatarImage src={getImageUrl(conv.profile?.profile_picture)} />
                            ) : (
                              <AvatarFallback className="bg-dark-subtle text-gold text-sm">
                                {conv.partner?.pseudo?.[0]?.toUpperCase() || '?'}
                              </AvatarFallback>
                            )}
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className={`font-medium truncate ${conv.is_system ? 'text-gold' : 'text-white'}`}>
                                {conv.partner?.pseudo || 'DiscreetLux'}
                              </span>
                              {conv.unread_count > 0 && (
                                <span className="w-5 h-5 bg-gold text-dark text-xs font-bold rounded-full flex items-center justify-center">
                                  {conv.unread_count}
                                </span>
                              )}
                            </div>
                            <p className="text-gray-500 text-xs truncate">
                              {conv.is_system ? 'Notifications système' : conv.last_message?.content}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}
          </div>

          {/* Desktop: Side by side layout */}
          <div className="hidden md:grid md:grid-cols-3 h-[600px]">
            {/* Conversations List */}
            <div className="border-r border-white/10">
              <div className="p-4 border-b border-white/10">
                <h2 className="text-white font-semibold">Conversations</h2>
              </div>
              <ScrollArea className="h-[calc(600px-57px)]">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-6 h-6 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Aucune conversation</p>
                  </div>
                ) : (
                  <div data-testid="conversation-list">
                    {conversations.map((conv) => (
                      <div
                        key={conv.partner?.id || 'system'}
                        className={`relative group ${
                          selectedConversation?.partner?.id === conv.partner?.id ? 'bg-gold/10 border-l-2 border-gold' : ''
                        }`}
                      >
                        <button
                          onClick={() => setSelectedConversation(conv)}
                          className="w-full p-4 flex items-center gap-3 hover:bg-white/5 transition-colors text-left"
                          data-testid={`conversation-${conv.partner?.id || 'system'}`}
                        >
                          <Avatar className={`w-12 h-12 border ${conv.is_system ? 'border-gold' : 'border-white/10'}`}>
                            {conv.is_system ? (
                              <AvatarFallback className="bg-gold/20 text-gold">
                                <Bell className="w-5 h-5" />
                              </AvatarFallback>
                            ) : conv.profile?.profile_picture ? (
                              <AvatarImage src={getImageUrl(conv.profile?.profile_picture)} />
                            ) : (
                              <AvatarFallback className="bg-dark-subtle text-gold">
                                {conv.partner?.pseudo?.[0]?.toUpperCase() || '?'}
                              </AvatarFallback>
                            )}
                          </Avatar>
                          {/* Online indicator on avatar */}
                          {!conv.is_system && conv.is_online && (
                            <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full ring-2 ring-dark" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className={`font-semibold truncate inline-flex items-center gap-1.5 ${conv.is_system ? 'text-gold' : 'text-white'}`}>
                                {conv.partner?.pseudo || 'DiscreetLux'}
                                {!conv.is_system && conv.is_online && (
                                  <OnlineIndicator isOnline={true} size="sm" />
                                )}
                              </span>
                              {conv.unread_count > 0 && (
                                <span className="w-5 h-5 bg-gold text-dark text-xs font-bold rounded-full flex items-center justify-center">
                                  {conv.unread_count}
                                </span>
                              )}
                            </div>
                            <p className="text-gray-500 text-sm truncate">
                              {conv.is_system ? 'Notifications système' : conv.last_message?.content}
                            </p>
                          </div>
                        </button>
                        
                        {/* Delete button - only for non-system conversations */}
                        {!conv.is_system && (
                          <button
                            onClick={(e) => deleteConversation(e, conv.partner?.id)}
                            disabled={deletingConversation === conv.partner?.id}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-full opacity-0 group-hover:opacity-100 transition-all"
                            title="Supprimer la conversation"
                            data-testid={`delete-conversation-${conv.partner?.id}`}
                          >
                            {deletingConversation === conv.partner?.id ? (
                              <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Messages Area */}
            <div className="md:col-span-2 flex flex-col h-[600px]">
              {selectedConversation ? (
                <>
                  {/* Header */}
                  <div className="p-4 border-b border-white/10 flex items-center gap-3 flex-shrink-0">
                    {selectedConversation.is_system ? (
                      <>
                        <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center">
                          <Bell className="w-5 h-5 text-gold" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gold">DiscreetLux</h3>
                          <p className="text-xs text-gray-500">Notifications système</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <Link to={`/member/${selectedConversation.partner.id}`} className="hover:opacity-80 transition-opacity">
                          <Avatar className="w-10 h-10 border border-gold/30">
                            {selectedConversation.profile?.profile_picture ? (
                              <AvatarImage src={getImageUrl(selectedConversation.profile?.profile_picture)} />
                            ) : (
                              <AvatarFallback className="bg-dark-subtle text-gold">
                                {selectedConversation.partner.pseudo?.[0]?.toUpperCase()}
                              </AvatarFallback>
                            )}
                          </Avatar>
                        </Link>
                        <div>
                          <Link 
                            to={`/member/${selectedConversation.partner.id}`}
                            className="font-semibold text-white hover:text-gold transition-colors"
                            data-testid="chat-partner-name"
                          >
                            {selectedConversation.partner.pseudo}
                          </Link>
                          <p className="text-xs text-gray-500">{selectedConversation.profile?.location}</p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Messages - with proper scrolling */}
                  <div 
                    className="flex-1 overflow-y-auto p-4 scrollbar-visible" 
                    style={{ maxHeight: 'calc(600px - 130px)', minHeight: '200px' }}
                  >
                    <div className="space-y-4" data-testid="messages-list">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${
                            (msg._isOwn === true || msg.sender_id === user.id || msg.sender_pseudo === user.pseudo) ? 'justify-end' : 'justify-start'
                          }`}
                        >
                          {msg.message_type === 'validation_request' ? (
                            renderValidationMessage(msg)
                          ) : msg.message_type === 'system_notification' || msg.sender_id === 'system' ? (
                            renderSystemNotification(msg)
                          ) : (
                            <div
                              className={`max-w-[70%] p-3 rounded-lg ${
                                (msg._isOwn === true || msg.sender_id === user.id || msg.sender_pseudo === user.pseudo)
                                  ? 'bg-gold text-dark'
                                  : 'bg-dark-subtle text-white'
                              }`}
                            >
                              <p className="text-sm">{msg.content}</p>
                              <span className={`text-xs mt-1 block ${
                                (msg._isOwn === true || msg.sender_id === user.id || msg.sender_pseudo === user.pseudo) ? 'text-dark/60' : 'text-gray-500'
                              }`}>
                                {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Input - only for non-system conversations and subscribers */}
                  {!selectedConversation.is_system && user.is_subscribed && (
                    <form onSubmit={sendMessage} className="p-4 border-t border-white/10 flex-shrink-0 bg-dark-paper">
                      <div className="flex items-center gap-2">
                        {/* Emoji picker button */}
                        <div className="relative" ref={emojiPickerRef}>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            className="p-2 text-gray-400 hover:text-gold hover:bg-gold/10"
                            data-testid="emoji-btn"
                          >
                            <Smile className="w-5 h-5" />
                          </Button>
                          
                          {showEmojiPicker && (
                            <div className="absolute bottom-12 left-0 z-50">
                              <EmojiPicker
                                onEmojiClick={onEmojiClick}
                                theme="dark"
                                width={320}
                                height={400}
                                searchPlaceholder="Rechercher un emoji..."
                                previewConfig={{ showPreview: false }}
                              />
                            </div>
                          )}
                        </div>
                        
                        {/* Message input */}
                        <Input
                          ref={inputRef}
                          type="text"
                          placeholder="Écrivez votre message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          className="flex-1 bg-dark-subtle border-white/20 text-white placeholder:text-gray-500"
                          disabled={sending}
                          data-testid="message-input"
                        />
                        
                        {/* Send button */}
                        <Button 
                          type="submit" 
                          className="btn-gold px-4"
                          disabled={sending || !newMessage.trim()}
                          data-testid="send-message-btn"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </form>
                  )}
                  
                  {/* Message bar for system conversations - info only */}
                  {selectedConversation.is_system && (
                    <div className="p-4 border-t border-white/10 bg-dark-paper/50 flex-shrink-0">
                      <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
                        <Bell className="w-4 h-4" />
                        <span>Les notifications système ne nécessitent pas de réponse</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Upgrade prompt for non-subscribers trying to message */}
                  {!selectedConversation.is_system && !canMessage && (
                    <div className="p-4 border-t border-white/10 bg-gold/5">
                      <div className="flex items-center justify-center gap-3">
                        <Lock className="w-5 h-5 text-gold" />
                        <span className="text-gray-400 text-sm">Abonnez-vous pour envoyer des messages</span>
                        <Button 
                          onClick={() => setShowSubscriptionPopup(true)}
                          className="btn-gold text-sm py-1 px-3"
                        >
                          <Crown className="w-4 h-4 mr-1" />
                          Ambassadeur
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Sélectionnez une conversation</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
